import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Plus, Edit, Trash2, Search, MapPin, Phone, Store as StoreIcon} from 'lucide-react'
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

interface ConvenienceStore {
  _id: string
  name: string
  nameEn: string
  brand: string
  address: string
  phone: string
  city: string
  district: string
  status: "active" | "inactive"
  createdAt: string
  updatedAt: string
}

const ConvenienceStoreManager: React.FC = () => {
  const [stores, setStores] = useState<ConvenienceStore[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterBrand, setFilterBrand] = useState("all")
  const [showForm, setShowForm] = useState(false)
  const [editingStore, setEditingStore] = useState<ConvenienceStore | null>(null)

  useEffect(() => {
    fetchStores()
  }, [])

  const fetchStores = async () => {
    setLoading(true)
    try {
      const response = await lumi.entities.convenience_stores.list({
        sort: { brand: 1, name: 1 }
      })
      setStores(response.list || [])
    } catch (error) {
      console.error("Failed to fetch stores:", error)
      toast.error("載入超商失敗")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    
    const storeData = {
      name: formData.get("name") as string,
      nameEn: formData.get("nameEn") as string,
      brand: formData.get("brand") as string,
      address: formData.get("address") as string,
      phone: formData.get("phone") as string,
      city: formData.get("city") as string,
      district: formData.get("district") as string,
      status: formData.get("status") as "active" | "inactive",
      createdAt: editingStore?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    try {
      if (editingStore) {
        await lumi.entities.convenience_stores.update(editingStore._id, storeData)
        toast.success("超商更新成功")
      } else {
        await lumi.entities.convenience_stores.create(storeData)
        toast.success("超商創建成功")
      }
      
      setShowForm(false)
      setEditingStore(null)
      fetchStores()
    } catch (error) {
      console.error("Store operation failed:", error)
      toast.error(editingStore ? "超商更新失敗" : "超商創建失敗")
    }
  }

  const handleEdit = (store: ConvenienceStore) => {
    setEditingStore(store)
    setShowForm(true)
  }

  const handleDelete = async (storeId: string) => {
    if (!confirm("確定要刪除這個超商嗎？")) return

    try {
      await lumi.entities.convenience_stores.delete(storeId)
      toast.success("超商刪除成功")
      fetchStores()
    } catch (error) {
      console.error("Delete failed:", error)
      toast.error("超商刪除失敗")
    }
  }

  const handleCancelForm = () => {
    setShowForm(false)
    setEditingStore(null)
  }

  const filteredStores = stores.filter(store => {
    const matchesSearch = store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         store.nameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         store.address.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === "all" || store.status === filterStatus
    const matchesBrand = filterBrand === "all" || store.brand === filterBrand
    return matchesSearch && matchesStatus && matchesBrand
  })

  const brands = Array.from(new Set(stores.map(s => s.brand))).sort()

  return (
    <div className="p-8 min-h-screen bg-gray-900">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-light tracking-wider text-gray-100">超商門市管理</h2>
          <p className="text-gray-400 mt-2">管理所有超商取貨門市</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus size={20} />
          新增超商
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="搜尋超商名稱或地址..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-gray-600"
          />
        </div>
        
        <select
          value={filterBrand}
          onChange={(e) => setFilterBrand(e.target.value)}
          className="px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
        >
          <option value="all">全部品牌</option>
          {brands.map(brand => (
            <option key={brand} value={brand}>{brand}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-3 bg-gray-950 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
        >
          <option value="all">全部狀態</option>
          <option value="active">啟用中</option>
          <option value="inactive">已停用</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-950 border border-gray-800 p-4">
          <div className="text-2xl font-light text-gray-100">{stores.length}</div>
          <div className="text-sm text-gray-400">總門市數</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-4">
          <div className="text-2xl font-light text-green-400">
            {stores.filter(s => s.status === "active").length}
          </div>
          <div className="text-sm text-gray-400">啟用中</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-4">
          <div className="text-2xl font-light text-red-400">
            {stores.filter(s => s.status === "inactive").length}
          </div>
          <div className="text-sm text-gray-400">已停用</div>
        </div>
        <div className="bg-gray-950 border border-gray-800 p-4">
          <div className="text-2xl font-light text-blue-400">{brands.length}</div>
          <div className="text-sm text-gray-400">品牌數量</div>
        </div>
      </div>

      {/* Store List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">載入中...</div>
      ) : (
        <div className="bg-gray-950 border border-gray-800">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-800">
                <tr className="text-left">
                  <th className="px-6 py-4 text-sm font-light text-gray-400">品牌</th>
                  <th className="px-6 py-4 text-sm font-light text-gray-400">門市名稱</th>
                  <th className="px-6 py-4 text-sm font-light text-gray-400">地址</th>
                  <th className="px-6 py-4 text-sm font-light text-gray-400">電話</th>
                  <th className="px-6 py-4 text-sm font-light text-gray-400">狀態</th>
                  <th className="px-6 py-4 text-sm font-light text-gray-400">操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredStores.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      沒有找到超商門市
                    </td>
                  </tr>
                ) : (
                  filteredStores.map((store) => (
                    <motion.tr
                      key={store._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-gray-800 hover:bg-gray-900 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-blue-600 text-white text-xs">
                          {store.brand}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-100">{store.name}</div>
                        <div className="text-sm text-gray-500">{store.nameEn}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        <div className="flex items-start gap-2">
                          <MapPin size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                          <span>{store.address}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-gray-500" />
                          <span>{store.phone}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 text-xs ${
                            store.status === "active"
                              ? "bg-green-600 text-white"
                              : "bg-gray-700 text-gray-300"
                          }`}
                        >
                          {store.status === "active" ? "啟用" : "停用"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(store)}
                            className="p-2 text-blue-400 hover:bg-blue-950 transition-colors"
                            title="編輯"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(store._id)}
                            className="p-2 text-red-400 hover:bg-red-950 transition-colors"
                            title="刪除"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-950 border border-gray-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-800">
              <h3 className="text-2xl font-light text-gray-100">
                {editingStore ? "編輯超商" : "新增超商"}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    門市名稱（中文）*
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingStore?.name}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    門市名稱（英文）*
                  </label>
                  <input
                    type="text"
                    name="nameEn"
                    defaultValue={editingStore?.nameEn}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">品牌*</label>
                  <select
                    name="brand"
                    defaultValue={editingStore?.brand}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  >
                    <option value="">選擇品牌</option>
                    <option value="7-11">7-11</option>
                    <option value="FamilyMart">FamilyMart</option>
                    <option value="HiLife">HiLife</option>
                    <option value="OK Mart">OK Mart</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">電話*</label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={editingStore?.phone}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">城市*</label>
                  <input
                    type="text"
                    name="city"
                    defaultValue={editingStore?.city}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">區域*</label>
                  <input
                    type="text"
                    name="district"
                    defaultValue={editingStore?.district}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">地址*</label>
                <input
                  type="text"
                  name="address"
                  defaultValue={editingStore?.address}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">狀態*</label>
                <select
                  name="status"
                  defaultValue={editingStore?.status || "active"}
                  required
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:outline-none focus:border-gray-600"
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                </select>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="px-6 py-3 bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  {editingStore ? "更新" : "創建"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default ConvenienceStoreManager
